import { useEffect, useMemo, useState } from "react";
import { setPage, useHub } from "../../../../core/Global";
import Icon from "../../../../components/base/Icon";
import FlexRow from "../../../../components/base/FlexRow";
import Indicator from "../../../../components/base/Indicator";
import DropDown from "../../../../components/form/DropDown";
import { FontAwesomeIcons } from "../../../../core/Types";
import { CallsAnalyticsResult } from "../models/CallsAnalytics";
import CallVolumeChart from "../components/CallVolumeChart";
import CallOutcomesDonut from "../components/CallOutcomesDonut";
import HourlyPatternChart from "../components/HourlyPatternChart";

const pad = (n: number) => String(n).padStart(2, "0");
const fmt = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
const pretty = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function computeRange(preset: string) {
    const end = new Date();
    const start = new Date();
    if (preset === "today") start.setHours(0, 0, 0, 0);
    else if (preset === "7") start.setDate(end.getDate() - 6);
    else if (preset === "90") start.setDate(end.getDate() - 89);
    else if (preset === "month") start.setDate(1);
    else start.setDate(end.getDate() - 29);
    const days = Math.round((end.getTime() - new Date(start).setHours(0, 0, 0, 0)) / 86400000) + 1;
    return { start: fmt(start), end: fmt(end), label: `${pretty(start)} – ${pretty(end)}`, days };
}

const PRESETS = [
    { id: "today", name: "Today" },
    { id: "7", name: "Last 7 days" },
    { id: "30", name: "Last 30 days" },
    { id: "90", name: "Last 90 days" },
    { id: "month", name: "This month" },
];
const DIRECTIONS = [
    { id: "All", name: "All Directions" },
    { id: "Incoming", name: "Inbound" },
    { id: "Outgoing", name: "Outbound" },
];
const STATUSES = [
    { id: "All", name: "All Status" },
    { id: "ANSWERED", name: "Answered" },
    { id: "NO ANSWER", name: "No Answer" },
    { id: "BUSY", name: "Busy" },
    { id: "FAILED", name: "Failed" },
];

const TABS: { id: string; name: string; icon: FontAwesomeIcons }[] = [
    { id: "overview", name: "Overview", icon: "chart-simple" },
    { id: "agents", name: "Agents", icon: "user-headset" },
    { id: "patterns", name: "Patterns", icon: "wave-pulse" },
    { id: "groups", name: "Groups", icon: "users" },
    { id: "numbers", name: "Phone Numbers", icon: "phone" },
];

interface KpiProps {
    icon: FontAwesomeIcons; label: string; value: string; color: string; sub?: string;
}
function KpiCard({ icon, label, value, color, sub }: KpiProps) {
    return (
        <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center p-4">
                <div className={`symbol symbol-45px me-3`}>
                    <span className={`symbol-label bg-light-${color}`}>
                        <Icon icon={icon} type="solid" size={18} className={`text-${color}`} />
                    </span>
                </div>
                <div>
                    <div className="fs-2 fw-bolder text-gray-900 lh-1">{value}</div>
                    <div className="fs-8 text-gray-500 text-uppercase fw-bold mt-1">{label}</div>
                    {sub && <div className="fs-8 text-gray-400 mt-1">{sub}</div>}
                </div>
            </div>
        </div>
    );
}

export default function CallsReport() {

    const hub = useHub();

    const [preset, presetSet] = useState<string>("30");
    const [direction, directionSet] = useState<string>("All");
    const [status, statusSet] = useState<string>("All");
    const [tab, tabSet] = useState<string>("overview");

    const [data, dataSet] = useState<CallsAnalyticsResult>();
    const [loading, loadingSet] = useState<boolean>(true);

    useEffect(() => { setPage("Call Reports"); }, []);

    const range = useMemo(() => computeRange(preset), [preset]);

    useEffect(() => {
        loadingSet(true);
        hub.Post("/reports/calls/analytics", {
            startDate: range.start,
            toDate: range.end,
            direction,
            status
        }, res => {
            if (res.success) dataSet(res.data);
            loadingSet(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preset, direction, status]);

    const k = data?.kpis;
    const change = k?.totalChangePercent;

    return (
        <div className="p-6">

            {/* Header */}
            <FlexRow className="mb-5">
                <div>
                    <h1 className="fs-1 fw-bolder text-gray-900 mb-1">Call Analytics</h1>
                    <div className="fs-7 text-gray-500">
                        <Icon icon="calendar-range" className="me-2" size={12} />
                        {range.label} · {range.days} day{range.days > 1 ? "s" : ""}
                    </div>
                </div>
            </FlexRow>

            {/* Filters */}
            <div className="card border-0 shadow-sm mb-5">
                <div className="card-body py-4">
                    <div className="row g-3">
                        <div className="col-sm-6 col-lg-3">
                            <DropDown id="cr-range" label="Date Range" labelUp deselect={false}
                                source={PRESETS} defaultValue={preset} onSelect={v => presetSet(v)} />
                        </div>
                        <div className="col-sm-6 col-lg-3">
                            <DropDown id="cr-direction" label="Direction" labelUp deselect={false}
                                source={DIRECTIONS} defaultValue={direction} onSelect={v => directionSet(v)} />
                        </div>
                        <div className="col-sm-6 col-lg-3">
                            <DropDown id="cr-status" label="Status" labelUp deselect={false}
                                source={STATUSES} defaultValue={status} onSelect={v => statusSet(v)} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="d-flex border-bottom mb-5 overflow-auto">
                {
                    TABS.map(t =>
                        <button key={t.id} type="button"
                            className={"btn btn-sm fw-bold border-0 rounded-0 px-4 " + (tab === t.id ? "text-primary border-bottom border-2 border-primary" : "text-gray-600")}
                            onClick={() => tabSet(t.id)}>
                            <Icon icon={t.icon} className="me-2" size={13} />
                            {t.name}
                        </button>
                    )
                }
            </div>

            {
                loading ?
                    <div className="py-20"><Indicator text="Loading analytics..." /></div>
                    :
                    tab !== "overview" ?
                        <div className="d-flex flex-column align-items-center justify-content-center text-center py-20">
                            <Icon icon="screwdriver-wrench" type="solid" size={36} className="text-gray-400 mb-4" />
                            <div className="fs-4 fw-bold text-gray-700">{TABS.find(t => t.id === tab)?.name} view is coming soon</div>
                            <div className="fs-7 text-gray-500 mt-2">The Overview tab is available now; this breakdown will be added next.</div>
                        </div>
                        :
                        <>
                            {/* Headline KPIs */}
                            <div className="row g-4 mb-4">
                                <div className="col-lg-6">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body d-flex align-items-center">
                                            <div className="symbol symbol-50px me-4">
                                                <span className="symbol-label bg-light-primary">
                                                    <Icon icon="phone-volume" type="solid" size={22} className="text-primary" />
                                                </span>
                                            </div>
                                            <div>
                                                <div className="fs-2x fw-bolder text-gray-900 lh-1">{k?.totalCalls ?? 0}</div>
                                                <div className="fs-7 text-gray-500 fw-bold mt-1">Total Calls</div>
                                            </div>
                                            {
                                                change !== null && change !== undefined &&
                                                <div className="ms-auto text-end">
                                                    <span className={"badge fw-bold " + (change >= 0 ? "badge-light-success" : "badge-light-danger")}>
                                                        <Icon icon={change >= 0 ? "arrow-up" : "arrow-down"} size={10} className="me-1" />
                                                        {Math.abs(change)}%
                                                    </span>
                                                    <div className="fs-8 text-gray-400 mt-1">vs previous period</div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body">
                                            <FlexRow>
                                                <div>
                                                    <div className="fs-2x fw-bolder text-gray-900 lh-1">{k?.answerRate ?? 0}%</div>
                                                    <div className="fs-7 text-gray-500 fw-bold mt-1">Answer Rate</div>
                                                </div>
                                                <div className="ms-auto fs-8 text-gray-400">
                                                    {k?.answered ?? 0} of {k?.totalCalls ?? 0} answered
                                                </div>
                                            </FlexRow>
                                            <div className="progress h-8px bg-light-success mt-4">
                                                <div className="progress-bar bg-success" role="progressbar"
                                                    style={{ width: `${k?.answerRate ?? 0}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* KPI grid */}
                            <div className="row g-4 mb-4">
                                <div className="col-6 col-md-4 col-xl"><KpiCard icon="phone-arrow-down-left" color="info" label="Inbound" value={`${k?.inbound ?? 0}`} /></div>
                                <div className="col-6 col-md-4 col-xl"><KpiCard icon="phone-arrow-up-right" color="primary" label="Outbound" value={`${k?.outbound ?? 0}`} /></div>
                                <div className="col-6 col-md-4 col-xl"><KpiCard icon="phone-missed" color="danger" label="Missed" value={`${k?.missed ?? 0}`} /></div>
                                <div className="col-6 col-md-4 col-xl"><KpiCard icon="hourglass-half" color="warning" label="Avg Duration" value={`${k?.avgDurationMinutes ?? 0}m`} /></div>
                                <div className="col-6 col-md-4 col-xl"><KpiCard icon="clock" color="success" label="Total Hours" value={`${k?.totalHours ?? 0}h`} /></div>
                                <div className="col-6 col-md-4 col-xl"><KpiCard icon="microphone" color="dark" label="Recorded" value={`${k?.recordedRate ?? 0}%`} sub={`${k?.recorded ?? 0} calls`} /></div>
                            </div>

                            {/* Charts row 1 */}
                            <div className="row g-4 mb-4">
                                <div className="col-lg-8">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header border-0 pt-5">
                                            <h3 className="fw-bold fs-4 text-gray-800">Call Volume Trend</h3>
                                        </div>
                                        <div className="card-body pt-2">
                                            <CallVolumeChart trend={data?.trend} />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header border-0 pt-5">
                                            <h3 className="fw-bold fs-4 text-gray-800">Call Outcomes</h3>
                                        </div>
                                        <div className="card-body pt-2 d-flex align-items-center">
                                            <CallOutcomesDonut outcomes={data?.outcomes} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Charts row 2 */}
                            <div className="row g-4">
                                <div className="col-lg-5">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header border-0 pt-5">
                                            <h3 className="fw-bold fs-4 text-gray-800">Top Agents by Volume</h3>
                                        </div>
                                        <div className="card-body pt-3">
                                            {
                                                (data?.topAgents?.length ?? 0) === 0 ?
                                                    <div className="text-gray-500 fs-7 py-10 text-center">No call data for this period.</div>
                                                    :
                                                    data!.topAgents.map((a, i) =>
                                                        <div key={i} className="mb-4">
                                                            <FlexRow className="mb-1">
                                                                <span className="fs-7 fw-semibold text-gray-700">{a.name}</span>
                                                                <span className="ms-auto fs-8 text-gray-500">{a.count} calls</span>
                                                            </FlexRow>
                                                            <div className="progress h-6px bg-light-primary">
                                                                <div className="progress-bar bg-primary" style={{ width: `${a.percent}%` }} />
                                                            </div>
                                                        </div>
                                                    )
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-7">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header border-0 pt-5">
                                            <h3 className="fw-bold fs-4 text-gray-800">Hourly Call Pattern</h3>
                                        </div>
                                        <div className="card-body pt-2">
                                            <HourlyPatternChart hours={data?.hourlyPattern} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
            }
        </div>
    );
}
